import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { ApiClient } from '@luacentrix/api-client';
import type { AuthIdentity } from '@luacentrix/contracts';
import type { ApiLanguage } from '@luacentrix/shared';
import { createApi, readLanguage, resolveEnv, writeLanguage } from './data/api';
import {
  checkSecurityPermission,
  grantedSecurityPermissions,
  type SecurityDecision,
  type SecurityPermission,
} from './data/security-access';
import { useAccountSession } from './data/queries';
import { applyTheme, otherTheme, readTheme, type ThemeName } from './data/theme';
import {
  applyUiStyle,
  glassCapability,
  readUiStyle,
  type GlassCapability,
  type UiStyleName,
} from './data/ui-style';

export type AccountStatus = 'pending' | 'signed_in' | 'sign_in_required';

export interface AccountState {
  status: AccountStatus;
  identity: AuthIdentity | null;
  permissions: SecurityPermission[];
  isFetching: boolean;
  has: (permission: SecurityPermission) => boolean;
  decisionFor: (permission: SecurityPermission) => SecurityDecision;
  refresh: () => void;
}

export interface AppState {
  api: ApiClient;
  language: ApiLanguage;
  setLanguage: (next: ApiLanguage) => void;
  theme: ThemeName;
  setTheme: (next: ThemeName) => void;
  toggleTheme: () => void;
  uiStyle: UiStyleName;
  setUiStyle: (next: UiStyleName) => void;
  glass: GlassCapability;
  account: AccountState;
}

const AppContext = createContext<AppState | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<ApiLanguage>(() => readLanguage());
  const [theme, setThemeState] = useState<ThemeName>(() => readTheme());
  const [uiStyle, setUiStyleState] = useState<UiStyleName>(() => readUiStyle());
  const glass = useMemo(() => glassCapability(), []);
  const env = useMemo(() => resolveEnv(import.meta.env as unknown as Record<string, unknown>), []);

  const api = useMemo(() => createApi(env, language), [env, language]);
  const session = useAccountSession(api);
  const identity = session.data?.identity ?? null;
  const isPending = session.isPending;
  const isFetching = session.isFetching;
  const refetchSession = session.refetch;

  const account = useMemo<AccountState>(() => {
    const reference = Date.now();
    const usable = identity !== null && (identity.expires_at === null || identity.expires_at > reference);
    const authority = usable ? identity : null;
    const status: AccountStatus = isPending ? 'pending' : usable ? 'signed_in' : 'sign_in_required';
    return {
      status,
      identity: authority,
      permissions: grantedSecurityPermissions(authority),
      isFetching,
      has: (permission: SecurityPermission) =>
        checkSecurityPermission(authority, permission, reference).allowed,
      decisionFor: (permission: SecurityPermission) =>
        checkSecurityPermission(authority, permission, reference),
      refresh: () => {
        void refetchSession();
      },
    };
  }, [identity, isFetching, isPending, refetchSession]);

  const setLanguage = useCallback((next: ApiLanguage) => {
    setLanguageState(next);
    writeLanguage(next);
  }, []);

  const setTheme = useCallback((next: ThemeName) => {
    setThemeState(next);
    applyTheme(next);
  }, []);

  const setUiStyle = useCallback((next: UiStyleName) => {
    setUiStyleState(next);
    applyUiStyle(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(otherTheme(theme));
  }, [setTheme, theme]);

  const value = useMemo<AppState>(
    () => ({
      api,
      language,
      setLanguage,
      theme,
      setTheme,
      toggleTheme,
      uiStyle,
      setUiStyle,
      glass,
      account,
    }),
    [api, language, setLanguage, theme, setTheme, toggleTheme, uiStyle, setUiStyle, glass, account],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppState {
  const value = useContext(AppContext);
  if (value === null) {
    throw new Error('useApp must be used inside AppProvider');
  }
  return value;
}
